import React from 'react';
import { ListRenderItem, Pressable, StyleSheet, View } from 'react-native';

import { UserAvatar } from '@components/Avatar';
import Badge from '@components/Badge';
import Divider from '@components/Divider';
import FlatList from '@components/FlatList';
import IconButton from '@components/IconButton';
import Placeholder from '@components/Placeholders';
import ScreenContainer from '@components/ScreenContainer';
import { Caption, Paragraph, Subheading } from '@components/Typography';
import { useChats } from '@context/ChatsContext';
import { useAppTheme } from '@context/ThemeContext';
import FontAwesome6 from '@expo/vector-icons/FontAwesome6';
import { dateFormatDistanceToNowStrict } from '@helpers/Dates';
import { filterChatUserInfo } from '@helpers/Formatters';
import { getCurrentUserId } from '@helpers/firebase/Auth';
import { AppNavigation, createAppNavigation } from '@navigation/AppNavigation';
import { ChatThreadsScreenProps } from '@navigation/Types';
import { Chat } from '@types';

export default function ChatThreadsScreen({ navigation }: ChatThreadsScreenProps) {
  const appNavigation = React.useMemo(() => createAppNavigation(navigation), [navigation]);

  React.useLayoutEffect(() => {
    navigation.setOptions({
      headerRight: () => (
        <IconButton
          icon={({ size, color }) => <FontAwesome6 name="edit" size={size} color={color} />}
          onPress={appNavigation.openCreateChat}
          testID="screens.ChatThreadsScreen.IconButton.CreateChat"
        />
      ),
    });
  }, [appNavigation.openCreateChat, navigation]);

  const { chats } = useChats();
  const visibleChats = React.useMemo(() => chats.filter((chat) => chat.latestMessage), [chats]);

  const renderItem: ListRenderItem<Chat> = ({ item }) => (
    <ChatThreadItem appNavigation={appNavigation} chatDoc={item} />
  );

  const keyExtractor = (item: Chat) => item.chatId;

  const renderItemSeparator = () => <Divider />;

  return (
    <ScreenContainer>
      <FlatList
        data={visibleChats}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        style={styles.list}
        initialNumToRender={15}
        ItemSeparatorComponent={renderItemSeparator}
        ListEmptyComponent={
          <Placeholder
            title="No chats"
            icon="message-text-outline"
            action="Start chatting"
            onPress={appNavigation.openCreateChat}
          />
        }
      />
    </ScreenContainer>
  );
}

type ChatThreadItemProps = {
  appNavigation: Pick<AppNavigation, 'openChat'>;
  chatDoc: Chat;
};

const AVATAR_SIZE = 50;
const MULTI_AVATAR_SIZE = AVATAR_SIZE - 10;

function ChatThreadItem({ appNavigation, chatDoc }: ChatThreadItemProps) {
  const appTheme = useAppTheme();
  const currentUserId = getCurrentUserId();
  const users = React.useMemo(
    () => filterChatUserInfo(chatDoc.userDocs, currentUserId),
    [chatDoc.userDocs, currentUserId]
  );

  const onPress = () => appNavigation.openChat(chatDoc.chatId);

  const numberOfLines = 1;

  const renderAvatar = () => (
    <View style={styles.avatarContainer}>
      {users.length > 1 ? (
        <>
          <View
            style={[
              styles.multiAvatar1,
              {
                borderColor: appTheme.colors.background,
              },
            ]}>
            {users[0] ? (
              <UserAvatar
                displayName={users[0].displayName}
                photoUrl={users[0].photoUrl}
                size={MULTI_AVATAR_SIZE}
              />
            ) : null}
          </View>
          <View style={styles.multiAvatar2}>
            {users[1] ? (
              <UserAvatar
                displayName={users[1].displayName}
                photoUrl={users[1].photoUrl}
                size={MULTI_AVATAR_SIZE}
              />
            ) : null}
          </View>
        </>
      ) : users.length > 0 ? (
        <UserAvatar
          displayName={users[0].displayName}
          photoUrl={users[0].photoUrl}
          size={AVATAR_SIZE}
        />
      ) : null}
    </View>
  );

  return (
    <Pressable style={styles.container} onPress={onPress} testID={chatDoc.chatId}>
      <View style={styles.threadContainer}>
        {chatDoc.unreadMessageCount[currentUserId] > 0 ? (
          <View style={styles.badgeContainer}>
            <Badge>{chatDoc.unreadMessageCount[currentUserId]}</Badge>
          </View>
        ) : null}
        {renderAvatar()}
        <View style={styles.textContainer}>
          <Subheading numberOfLines={numberOfLines}>
            {users.map((user) => user.displayName).join(', ')}
          </Subheading>
          <View style={styles.messageTextContainer}>
            <Paragraph
              numberOfLines={numberOfLines}
              style={[styles.paragraph, { color: appTheme.colors.placeholder }]}>
              {chatDoc.latestMessage ? chatDoc.latestMessage.text : 'Send a message'}
            </Paragraph>
            <Caption style={styles.caption}>
              {chatDoc.latestMessage
                ? dateFormatDistanceToNowStrict(chatDoc.latestMessage.createdAt) + ' ago'
                : ''}
            </Caption>
          </View>
        </View>
      </View>
    </Pressable>
  );
}

const styles = StyleSheet.create({
  avatarContainer: {
    alignItems: 'center',
    height: AVATAR_SIZE,
    justifyContent: 'center',
    marginRight: 10,
    marginVertical: 5,
    width: AVATAR_SIZE,
  },
  badgeContainer: {
    justifyContent: 'center',
    paddingRight: 10,
  },
  caption: {
    textAlign: 'right',
    textAlignVertical: 'center',
  },
  container: {
    justifyContent: 'center',
    marginHorizontal: 5,
    padding: 5,
  },
  list: {
    width: '100%',
  },
  messageTextContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  multiAvatar1: {
    borderRadius: MULTI_AVATAR_SIZE,
    borderWidth: 3,
    bottom: 0,
    position: 'absolute',
    right: -5,
    zIndex: 1,
  },
  multiAvatar2: {
    left: -5,
    position: 'absolute',
    top: 0,
  },
  paragraph: {
    marginBottom: 0,
    maxWidth: '65%',
    paddingRight: 10,
    textAlign: 'left',
    textAlignVertical: 'center',
  },
  textContainer: {
    flexDirection: 'column',
    flex: 1,
  },
  threadContainer: {
    flexDirection: 'row',
  },
});
